import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { ManageLocationDialogComponent } from 'src/app/components/main/shared/locations/manage-location-dialog/manage-location-dialog.component';
import { EmployeeCalendarRegisterRelatableType } from 'src/app/enums/employee-calendar-register-relatable-type.enum';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { DrivingRequest, DrivingRequestCompanyProductView, DrivingRequestRow } from 'src/app/models/driving_request.model';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { ApiService, HttpQueryParams } from 'src/app/services';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-my-driving-requests',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    RouterLink,
  ],
  templateUrl: './my-driving-requests.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./my-driving-requests.component.css'],
})
export class MyDrivingRequestsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  protected fb: UntypedFormBuilder;
  protected router: Router;
  protected activated_route: ActivatedRoute;
  protected utils: UtilsService;

  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;

  readonly displayed_columns: string[] = ['driver', 'trip_date', 'route', 'trip_distance', 'company_product', 'status', 'actions'];

  readonly data_source = new MatTableDataSource<DrivingRequestRow>([]);

  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly user = inject(UserService);

  protected readonly current_user_id: number = this.user.getCurrentUser().id;

  override filterFormExtraParams: { status: EmployeeRequestResponsibleForAnswerStatusEnum | '' } = {
    status: '',
  };

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const router = inject(Router);
    const activated_route = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activated_route, utils);

    this.fb = fb;
    this.router = router;
    this.activated_route = activated_route;
    this.utils = utils;
  }

  override fetchData(): void {
    const filter_status = this.filterForm.value.status as EmployeeRequestResponsibleForAnswerStatusEnum | '';

    const params: HttpQueryParams = {
      ...this.filterForm.value,
      with: 'employee_calendar_register.trip_driver_user,trip_driver_candidates,employee_calendar_register.origin_location,employee_calendar_register.destination_location,employee_calendar_register.relatable',
      order_by: 'created_at:desc',
      trip_driver_user_id: this.current_user_id,
    };

    if (filter_status) {
      params['status'] = filter_status;
    } else {
      delete params['status'];
    }

    this.api
      .getDrivingRequests(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (data: ArrayResponse<DrivingRequest>): void => {
          this.data_source.data = data.data.map((driving_request: DrivingRequest): DrivingRequestRow => this.toRow(driving_request));
          this.setMetadata(Number(data.meta.total), Number(data.meta.current_page) - 1, Number(data.meta.per_page));
        },
      });
  }

  openApprovalStatusDialog(driving_request: DrivingRequest): void {
    const data: EmployeeRequestApprovalStatusDialogData = {
      requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.DrivingRequest,
      requestable_id: driving_request.id,
      requestable_status: driving_request.status,
    };

    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data,
    });
  }

  openLocationDialog(location: Location): void {
    const data: ManageLocationDialogData = {
      mode: LocationDialogModeEnum.View,
      location,
    };

    this.dialog.open(ManageLocationDialogComponent, {
      width: '980px',
      data: data satisfies ManageLocationDialogData,
    });
  }

  private toRow(driving_request: DrivingRequest): DrivingRequestRow {
    return {
      request: driving_request,
      origin: driving_request.employee_calendar_register?.origin_location ?? null,
      destination: driving_request.employee_calendar_register?.destination_location ?? null,
      company_product: this.getCompanyProduct(driving_request),
      trip_date: this.formatTripDate(driving_request),
      trip_distance: this.formatTripDistance(driving_request),
      status_label: EmployeeRequestResponsibleForAnswerStatusLabels[driving_request.status],
      driver: this.formatDriver(driving_request),
    };
  }

  private formatDriver(driving_request: DrivingRequest): string {
    const resolved_driver_name = driving_request.employee_calendar_register?.trip_driver_user?.full_name;

    if (resolved_driver_name) {
      return resolved_driver_name;
    }

    const candidate_names = driving_request.trip_driver_candidates?.map(candidate => candidate.full_name).filter(Boolean) ?? [];

    return candidate_names.length > 0 ? candidate_names.join(', ') : '—';
  }

  private formatTripDate(driving_request: DrivingRequest): string {
    const from_date = driving_request.employee_calendar_register?.from_date;

    if (!from_date) {
      return '—';
    }

    const date = new Date(from_date);

    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private formatTripDistance(driving_request: DrivingRequest): string {
    const distance = driving_request.employee_calendar_register?.trip_distance_km;

    if (distance === null || distance === undefined) {
      return '—';
    }

    return `${distance.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} km`;
  }

  private getCompanyProduct(driving_request: DrivingRequest): DrivingRequestCompanyProductView | null {
    const ecr = driving_request.employee_calendar_register;

    if (!ecr?.relatable) {
      return null;
    }

    if (ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct) {
      const company_product = ecr.relatable as CompanyProduct;

      return {
        id: company_product.id,
        title: company_product.title ?? `Producto #${company_product.id}`,
        logo_url: company_product.logo_url ?? null,
      };
    }

    if (ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase) {
      const phase = ecr.relatable as CompanyProductPhase & { company_product?: CompanyProduct };
      const company_product_id = phase.company_product?.id ?? phase.company_product_id;

      if (company_product_id === undefined || company_product_id === null) {
        return null;
      }

      return {
        id: company_product_id,
        title: phase.company_product?.title ?? `Producto #${company_product_id}`,
        logo_url: phase.company_product?.logo_url ?? null,
      };
    }

    return null;
  }
}
